import React, { useState } from 'react';
import { API_BASE_URL } from '../api/client';
import { MarkdownText } from './MarkdownText';

const sections = [
  {
    title: 'Authentication',
    content: `All admin endpoints require a JWT token in the Authorization header:
\`Authorization: Bearer <token>\`

**Register (first user only):**
\`POST /api/auth/register\` — body: \`{ "username": "...", "password": "..." }\`

**Login:**
\`POST /api/auth/login\` — body: \`{ "username": "...", "password": "..." }\`
Returns: \`{ "access_token": "..." }\`

**Current user:**
\`GET /api/auth/me\` — requires auth token

**Check if setup needed:**
\`GET /api/auth/setup-required\` — no auth, returns \`{ "required": true/false }\`

**User management (admin):**
\`GET /api/auth/users\` — list users
\`POST /api/auth/users\` — create user: \`{ "username": "...", "password": "..." }\`
\`PUT /api/auth/users/:id\` — update user: \`{ "username?": "...", "password?": "..." }\`
\`DELETE /api/auth/users/:id\` — delete user`,
  },
  {
    title: 'Workspace API (S2S)',
    content: `Server-to-server endpoints for external apps and third-party UIs: manage the workspace's own agents with a workspace key, no admin login. Every route is scoped to the calling workspace — agents and templates of other workspaces answer \`404\`, as if they did not exist.

**Header:** \`x-workspace-key: ws_...\`

**List templates in workspace:**
\`GET /api/s2s/templates\`
Returns the public templates of this workspace: \`{ "id", "name", "description", "language", "customFields" }\`. Prompts and provider config stay admin-only.

**Get template:**
\`GET /api/s2s/templates/:id\` — same projection, \`404\` if the template is not public or not in this workspace.

**Create agent in workspace:**
\`POST /api/s2s/agents\`
Body:
\`\`\`json
{
  "templateId": "...",
  "name": "My Agent",
  "customFieldValues": { "company_name": "Acme" },
  "customPrompt": "Extra instructions",
  "greeting": "Hello!",
  "maxHistoryMessages": 10,
  "mcpServers": []
}
\`\`\`
- \`templateId\` — must be a public template of this workspace, otherwise \`400\`.
- The agent is stamped with this workspace automatically; the response includes its \`apiKey\` (\`ag_...\`) — the only time it is handed out, store it.

**List agents in workspace:**
\`GET /api/s2s/agents\`

**Get agent:**
\`GET /api/s2s/agents/:id\`

**Update agent:**
\`PUT /api/s2s/agents/:id\`
Body: \`{ "name", "greeting", "customPrompt", "customFieldValues", "maxHistoryMessages", "voiceMode", "mcpServers", "knowledgeBaseIds", "whatsapp", "templateId" }\` — all optional.
- Re-pointing \`templateId\` is allowed only to another public template of this workspace, otherwise \`400\`.

**Delete agent:**
\`DELETE /api/s2s/agents/:id\` — returns \`{ "ok": true }\`

**Agent sessions:**
\`GET /api/s2s/agents/:id/sessions\`

**Test the agent's MCP servers:**
\`POST /api/s2s/agents/:id/mcp-test\`
Returns: \`{ "serverCount": 1, "toolCount": 15, "tools": [...] }\`

**Note:** \`/api/agents/*\` and \`/api/knowledge-bases/*\` are admin-only (Bearer token) — a workspace key is not accepted there. Workspace consumers use these \`/api/s2s\` routes; an agent can also manage itself with its own key via \`/api/agents/me\`.`,
  },
  {
    title: 'Standard Mastra API (drop-in)',
    content: `The standard, drop-in Mastra server — any \`@mastra/client-js\` consumer (or Mastra-compatible tool) works out of the box, calling agents by credential. No bespoke client needed.

**Base URL:** \`http://<host>:<MASTRA_STD_PORT>/api/std\`
Runs on a dedicated port (default main \`PORT\`+1) as its own server. Expose \`/api/std\` via a reverse proxy to share the main public origin.

**Auth:** \`Authorization: Bearer ag_...\`
The agent key authenticates and authorizes only its own agent id — wrong key → \`401\`, wrong agent id → \`403\`.

**OpenAPI schema (public, no auth):**
\`GET /api/std/openapi.json\` — standard Mastra OpenAPI 3.1 spec.

**Generate (full response):**
\`POST /api/std/agents/:id/generate\`
Body: \`{ "messages": [ { "role": "system" | "user" | "assistant", "content": "..." } ] }\`
Returns: \`{ "text": "...", "usage": { "inputTokens": 0, "outputTokens": 0, "totalTokens": 0 } }\`

**Stream (SSE):**
\`POST /api/std/agents/:id/stream\` — \`text/event-stream\`, standard Mastra chunk frames.

**@mastra/client-js:**
\`\`\`ts
import { MastraClient } from '@mastra/client-js';
const agent = new MastraClient({
  baseUrl: 'http://<host>:<port>/api/std',
  headers: { Authorization: \`Bearer \${agentKey}\` },
}).getAgent(agentId);
const { text } = await agent.generate({ messages });
const stream = await agent.stream({ messages });
\`\`\`

**Stateless:** the server keeps no thread here — send the full \`messages[]\` every call. Inject per-turn context as a leading \`system\` message (out-of-band, un-spoofable). Server-side Memory stays on the internal \`/api/conversation/*\` paths.

**Hot registration:** agents created via the agent API are callable here without a restart.`,
  },
  {
    title: 'Normalized Agent Generate',
    content: `Mastra-shaped \`generate\` on the MAIN API with our header auth — for consumers already using \`x-agent-key\`/\`x-session-id\` (the same secure path as \`/conversation/text-chat\`). For streaming or the standard Mastra client, use the drop-in \`/api/std\` above.

**Headers:** \`x-agent-key: ag_...\`, \`x-session-id: <session-id>\`
Connect first via \`POST /api/agents/connect\` to obtain a session.

\`POST /api/agents/:id/generate\`
Body: \`{ "message": "Hello" }\` or \`{ "messages": [ { "role": "user", "content": "..." } ] }\`
- Only the latest user message is taken; prior turns come from server-side **Memory** (thread = session). Multi-turn \`messages[]\` are NOT replayed.
- \`400 Bad Request\` if no non-empty message is provided.

Returns:
\`\`\`json
{
  "text": "Hi there!",
  "usage": { "inputTokens": 120, "cachedInputTokens": 0, "outputTokens": 18, "totalTokens": 138 }
}
\`\`\`

No \`/stream\` on this route — use \`/api/std/agents/:id/stream\`.`,
  },
  {
    title: 'Agent Self-Service (/me)',
    content: `Agents can manage themselves using their own API key.

**Header:** \`x-agent-key: ag_...\`

**Get agent info:**
\`GET /api/agents/me\`

**Update agent:**
\`PUT /api/agents/me\`
Body: \`{ "name": "...", "greeting": "...", "customPrompt": "...", "maxHistoryMessages": 10 }\`

**Delete agent:**
\`DELETE /api/agents/me\``,
  },
  {
    title: 'MCP Integration',
    content: `Agents can connect to external MCP (Model Context Protocol) servers to use tools during conversations.

**Configure MCP servers on an agent:**
\`PUT /api/agents/:id\`
Body:
\`\`\`json
{
  "mcpServers": [
    {
      "name": "my-server",
      "url": "https://example.com/mcp",
      "auth": {
        "type": "client_credentials",
        "clientId": "...",
        "clientSecret": "..."
      }
    }
  ]
}
\`\`\`

**Auth types:**
- \`client_credentials\` — OAuth 2.1 flow: discovers token endpoint via .well-known, gets Bearer token
- \`none\` — no authentication (local servers)
- Custom headers — use the \`headers\` field for any custom auth

**Test MCP connection:**
\`POST /api/agents/:id/mcp-test\`
Returns: \`{ "serverCount": 1, "toolCount": 15, "tools": [...] }\`

**How it works:**
1. Agent connects to MCP server via Streamable HTTP transport
2. Server returns instructions (injected into LLM system prompt)
3. Tools are discovered and passed to LLM as functions
4. LLM calls tools during conversation, results fed back automatically
5. Tool call loop runs up to 10 rounds per user message

**Settings:**
- \`maxHistoryMessages\` — limits conversation history sent to LLM (default: 10). Older messages replaced with summary.`,
  },
  {
    title: 'Conversation API',
    content: `Process voice/text through the STT → LLM → TTS pipeline, or use the pure text-chat mode.

**Headers (for agent-scoped endpoints):**
\`x-agent-key: ag_...\`
\`x-session-id: <session-id>\`

**Get agent info (no session needed):**
\`POST /api/agents/info\`
Header: \`x-agent-key\`
Returns: \`{ "name": "...", "description": "...", "language": "...", "useStreaming": true }\`

**Connect (get session):**
\`POST /api/agents/connect\`
Header: \`x-agent-key\`
Body (optional):
\`\`\`json
{
  "mode": "voice" | "text",
  "source": "direct" | "twilio",
  "callbackUrl": "https://consumer/callback?token=secret",
  "context": "Customer John Smith, account #12345, calling about billing",
  "inactivityTimeout": 600
}
\`\`\`
- \`mode\` — \`"text"\` creates a text-only session (no STT/TTS). Default: \`"voice"\`.
- \`source\` — \`"twilio"\` marks the session for Twilio call lifecycle (cleanup polls Twilio for call status). Default: \`"direct"\`. Cannot combine \`source=twilio\` with \`mode=text\`.
- \`callbackUrl\` — opaque URL where live-agent POSTs real-time events (transcripts, transfer requests, session end). Consumer embeds routing/auth data in the URL query string.
- \`context\` — free-form string injected at the **top** of the AI system prompt as high-priority instructions. Use for caller info, case details, or behavioural directions (e.g. "do not greet — you are mid-call, continue the task"). When \`context\` is non-empty on a new session, live-agent calls the LLM to generate the opening line based on these instructions instead of using the template's static greeting.
- \`inactivityTimeout\` — seconds of inactivity (since last message) after which cleanup auto-ends the session. Default: 1800 (30 min).

Returns: \`{ "sessionId": "...", "sessionToken": "...", "agent": {...} }\`
- \`sessionToken\` — crypto-random token for WS authentication. Must be passed as \`<Parameter name="sessionToken">\` in TwiML. Required when reusing a pre-created session via WebSocket.

**Disconnect (end session):**
\`POST /api/agents/disconnect\`
Headers: \`x-agent-key\`, \`x-session-id\`
Returns: \`{ "ok": true }\`

**Process audio/voice:**
\`POST /api/conversation/agent-process\`
Content-Type: multipart/form-data
Body: \`audio\` (file) or \`text\` (string)
Response: audio blob with headers \`X-Transcribed-Text\`, \`X-Llm-Response-Text\`, \`X-Metrics\`

**Text chat (JSON in/out, no STT/TTS):**
\`POST /api/conversation/text-chat\`
Headers: \`x-agent-key\`, \`x-session-id\`, \`Content-Type: application/json\`
Body: \`{ "message": "Hello" }\`
- \`message\` — required, non-empty string, max 10000 characters.
Errors: \`400 Bad Request\` if \`message\` is missing, empty, or exceeds 10000 chars.
Returns:
\`\`\`json
{
  "response": "Hi there!",
  "metrics": {
    "llmLatencyMs": 420,
    "inputTokens": 120,
    "outputTokens": 18,
    "cost": 0.00032
  }
}
\`\`\`

**Integration flow (text chat):**
1. \`POST /api/agents/connect\` with \`{ "mode": "text", "inactivityTimeout": 300 }\` → get \`sessionId\`
2. \`POST /api/conversation/text-chat\` with \`{ "message": "..." }\` for each turn
3. \`POST /api/agents/disconnect\` when done (optional — cleanup ends it on inactivity)`,
  },
  {
    title: 'Standalone STT & TTS',
    content: `Direct access to speech-to-text and text-to-speech without the full pipeline.

**Auth:** \`Authorization: Bearer <agent API key>\` (or \`x-agent-key: <agent API key>\`). No session is needed — unlike the conversation endpoints, these are one-shot calls. An admin Bearer token also works.

**Transcribe audio:**
\`POST /api/stt/transcribe\`
Content-Type: multipart/form-data
Body: \`audio\` (file, max 10 MB), \`provider\` ("deepgram-nova-3" | "deepgram-flux" | "gladia"), \`language?\` (string)

**Streaming transcription (WebSocket):**
\`ws(s)://<host>/stt-stream?agentKey=ag_...&language=<iso>\`
Send: binary frames, PCM16LE mono @ 16 kHz.
Receive: \`{ "type": "transcript", "role": "user", "text": "..." }\` — one message per phrase, emitted when the speaker pauses; \`{ "type": "error", "message": "..." }\`.
Ears only: no session, no memory, no model — the transcript is the whole product. \`language\` overrides the agent template's ("en", "ru", "es"; "en-US" also accepted). Audio sent before the upstream is ready is buffered, not dropped.

**Synthesize speech:**
\`POST /api/tts/synthesize\`
Body: \`{ "text": "...", "provider": "openai-tts-1" | "cartesia-sonic-3" | "elevenlabs-multilingual-v2", "voice?": "..." }\`
Response: raw audio binary
Headers: \`X-Processing-Time-Ms\`, \`X-Character-Count\``,
  },
  {
    title: 'Twilio Integration',
    content: `For phone call support via Twilio Media Streams.

**Voice webhook (set in Twilio console):**
\`POST https://<your-url>/api/twilio/voice/<agentId>\`
Method: POST, returns TwiML

**WebSocket media stream:**
\`wss://<your-url>/media-stream/<agentId>\`
Twilio connects automatically via the TwiML response.

**Status callback (for session cleanup):**
\`POST https://<your-url>/api/twilio/status\`
Set as "Status Callback URL" in Twilio phone number config.

**Setup:**
1. Set \`TWILIO_BASE_URL\` env var to your public URL (ngrok for dev)
2. Configure Twilio phone number → Voice webhook URL
3. Configure Twilio phone number → Status Callback URL
4. Agent detail page shows the exact URLs to use`,
  },
  {
    title: 'Twilio Callback Integration',
    content: `Server-to-server webhook contract for consumers that use live-agent as their voice AI layer over Twilio Media Streams.

**Flow:**
1. Consumer calls \`POST /api/agents/connect\` with \`source: "twilio"\`, \`callbackUrl\`, and optional \`context\`.
2. Consumer builds TwiML pointing Twilio \`<Stream>\` at \`wss://<live-agent>/media-stream/<agentId>\` with \`sessionId\` parameter.
3. Live-agent POSTs real-time events to \`callbackUrl\`.
4. Consumer can take over the call at any moment by rewriting Twilio TwiML — live-agent notices the WS closing.

**TwiML example (consumer builds this):**
\`\`\`xml
<Response>
  <Connect>
    <Stream url="wss://<live-agent>/media-stream/<agentId>">
      <Parameter name="sessionId" value="<sessionId from connect>" />
      <Parameter name="sessionToken" value="<sessionToken from connect>" />
    </Stream>
  </Connect>
</Response>
\`\`\`

**Callback signature verification:**
Every callback includes an \`X-Signature\` header — HMAC-SHA256 of the raw JSON body, keyed by \`sessionToken\`.
Consumer verifies: \`HMAC-SHA256(rawBody, sessionToken) === X-Signature header\`.
This proves the callback is authentic without transmitting the token itself.

**Events POSTed to callbackUrl:**

Transcript (user or assistant speech):
\`\`\`json
{ "event": "transcript", "role": "user" | "assistant", "content": "...", "index": 1, "timestamp": "..." }
\`\`\`

Transfer request (AI called transfer_to_human tool):
\`\`\`json
{ "event": "transfer_request", "reason": "user asked for a manager", "timestamp": "..." }
\`\`\`

Session ended:
\`\`\`json
{ "event": "session_ended", "reason": "end_call_tool" | "ws_closed" | "ws_stop" | "consumer_ended" | "transfer_completed", "timestamp": "..." }
\`\`\`

**Consumer response semantics:**
- \`2xx\` — continue normally
- \`404\` — consumer session is gone; live-agent speaks farewell and hangs up
- Other \`4xx/5xx\` on transcript — log and ignore, keep conversation going
- \`4xx/5xx\` on transfer_request — AI apologizes and continues conversation
- Any response on session_ended — fire-and-forget

**Retries:** all events are retried up to 3 times with 3s delay (except 404 — not retried).

**transfer_to_human tool:**
Available to AI only when \`callbackUrl\` is set. On 2xx response: AI speaks farewell, then live-agent gracefully closes the WebSocket. Twilio fetches the \`<Connect action="...">\` URL — consumer routes the call there (to conference, hangup, etc.). Live-agent also emits \`session_ended\` with reason \`transfer_completed\`.

**Session reuse (Phase 2):**
When consumer returns the call to AI, they build a new TwiML \`<Stream>\` with the same \`sessionId\`. Live-agent reuses the session, skips greeting, AI continues with full conversation history.`,
  },
  {
    title: 'Public Endpoints',
    content: `No authentication required.

**Public config:**
\`GET /api/config/public\`
Returns: \`{ "twilioBaseUrl": "...", "backendUrl": "..." }\`

**List providers:**
\`GET /api/providers\`
Returns: \`{ "stt": [...], "tts": [...] }\`

**Public template catalog:**
\`GET /api/catalog/templates\` — list public templates
\`GET /api/catalog/templates/:id\` — get public template by id`,
  },
  {
    title: 'Templates & Agents (Admin)',
    content: `**Templates** — requires auth token.

\`POST /api/templates\` — create
\`GET /api/templates\` — list all
\`GET /api/templates/:id\` — get
\`PUT /api/templates/:id\` — update
\`DELETE /api/templates/:id\` — delete (cascades agents)

**Agents** — requires auth token. Workspace keys are not accepted here; use \`/api/s2s/agents\` instead.

\`POST /api/agents\` — create
\`GET /api/agents\` — list all
\`GET /api/agents/:id\` — get
\`PUT /api/agents/:id\` — update
\`DELETE /api/agents/:id\` — delete

**Sessions** — requires auth token.
\`GET /api/agents/:id/sessions\` — list sessions for agent
\`GET /api/agents/all-sessions\` — list all sessions

**Workspaces** — requires auth token.
\`POST /api/workspaces\` — create
\`GET /api/workspaces\` — list
\`GET /api/workspaces/:id\` — get
\`PUT /api/workspaces/:id\` — update
\`DELETE /api/workspaces/:id\` — delete`,
  },
];

// Markdown склеивает одиночные переносы в абзац, а доки на них держатся
// (эндпоинт под своим заголовком) — превращаем их в hard break вне ``` блоков.
function withHardBreaks(md: string): string {
  return md
    .split(/(```[\s\S]*?```)/g)
    .map((part) => (part.startsWith('```') ? part : part.replace(/([^\n])\n(?!\n)/g, '$1  \n')))
    .join('');
}

const renderedSections = sections.map((sec) => ({ ...sec, content: withHardBreaks(sec.content) }));

function buildPostmanCollection(): object {
  const baseUrl = '{{baseUrl}}';
  const mkReq = (method: string, path: string, headers: Record<string, string>[] = [], body?: object) => {
    const req: any = {
      method,
      header: headers.map(h => {
        const [key, value] = Object.entries(h)[0];
        return { key, value, type: 'text' };
      }),
      url: { raw: `${baseUrl}${path}`, host: ['{{baseUrl}}'], path: path.split('/').filter(Boolean) },
    };
    if (body) {
      req.body = { mode: 'raw', raw: JSON.stringify(body, null, 2), options: { raw: { language: 'json' } } };
      req.header.push({ key: 'Content-Type', value: 'application/json', type: 'text' });
    }
    return req;
  };
  const authH = { Authorization: 'Bearer {{token}}' };
  const agentH = { 'x-agent-key': '{{agentKey}}' };
  const wsH = { 'x-workspace-key': '{{workspaceKey}}' };

  return {
    info: { name: 'Eazyshow Live Agent', schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json' },
    variable: [
      { key: 'baseUrl', value: 'http://localhost:4001/api' },
      { key: 'stdBaseUrl', value: 'http://localhost:4002/api/std' },
      { key: 'token', value: '' },
      { key: 'agentKey', value: '' },
      { key: 'agentId', value: '' },
      { key: 'sessionId', value: '' },
      { key: 'workspaceKey', value: '' },
    ],
    item: [
      {
        name: 'Auth', item: [
          { name: 'Check setup required', request: mkReq('GET', '/auth/setup-required') },
          { name: 'Register', request: mkReq('POST', '/auth/register', [], { username: 'admin', password: 'password123' }) },
          { name: 'Login', request: mkReq('POST', '/auth/login', [], { username: 'admin', password: 'password123' }) },
          { name: 'Get current user', request: mkReq('GET', '/auth/me', [authH]) },
          { name: 'List users', request: mkReq('GET', '/auth/users', [authH]) },
          { name: 'Create user', request: mkReq('POST', '/auth/users', [authH], { username: 'newuser', password: 'password123' }) },
          { name: 'Update user', request: mkReq('PUT', '/auth/users/:id', [authH], { username: 'updated' }) },
          { name: 'Delete user', request: mkReq('DELETE', '/auth/users/:id', [authH]) },
        ],
      },
      {
        name: 'Templates (Admin)', item: [
          { name: 'Create template', request: mkReq('POST', '/templates', [authH], { name: 'My Template', sttProvider: 'deepgram-nova-3', ttsProvider: 'openai-tts-1', llmModel: 'gpt-4o-mini', baseSystemPrompt: 'You are a helpful assistant.' }) },
          { name: 'List templates', request: mkReq('GET', '/templates', [authH]) },
          { name: 'Get template', request: mkReq('GET', '/templates/:id', [authH]) },
          { name: 'Update template', request: mkReq('PUT', '/templates/:id', [authH], { name: 'Updated Template' }) },
          { name: 'Delete template', request: mkReq('DELETE', '/templates/:id', [authH]) },
        ],
      },
      {
        name: 'Agents (Admin)', item: [
          { name: 'Create agent', request: mkReq('POST', '/agents', [authH], { templateId: '...', name: 'My Agent' }) },
          { name: 'List agents', request: mkReq('GET', '/agents', [authH]) },
          { name: 'Get agent', request: mkReq('GET', '/agents/:id', [authH]) },
          { name: 'Update agent', request: mkReq('PUT', '/agents/:id', [authH], { name: 'Updated' }) },
          { name: 'Delete agent', request: mkReq('DELETE', '/agents/:id', [authH]) },
          { name: 'Agent sessions', request: mkReq('GET', '/agents/:id/sessions', [authH]) },
          { name: 'All sessions', request: mkReq('GET', '/agents/all-sessions', [authH]) },
          { name: 'Test MCP', request: mkReq('POST', '/agents/:id/mcp-test', [authH]) },
        ],
      },
      {
        name: 'Agent Self-Service', item: [
          { name: 'Get my info', request: mkReq('GET', '/agents/me', [agentH]) },
          { name: 'Update my agent', request: mkReq('PUT', '/agents/me', [agentH], { name: 'Updated', greeting: 'Hi!' }) },
          { name: 'Delete my agent', request: mkReq('DELETE', '/agents/me', [agentH]) },
        ],
      },
      {
        name: 'Conversation', item: [
          { name: 'Agent info', request: mkReq('POST', '/agents/info', [agentH]) },
          { name: 'Connect (voice)', request: mkReq('POST', '/agents/connect', [agentH], { mode: 'voice', inactivityTimeout: 1800 }) },
          { name: 'Connect (text)', request: mkReq('POST', '/agents/connect', [agentH], { mode: 'text', inactivityTimeout: 600 }) },
          { name: 'Connect (twilio + callback)', request: mkReq('POST', '/agents/connect', [agentH], { mode: 'voice', source: 'twilio', callbackUrl: 'https://consumer/callback?token=secret', context: 'Customer info here', inactivityTimeout: 900 }) },
          { name: 'Disconnect', request: mkReq('POST', '/agents/disconnect', [agentH, { 'x-session-id': '{{sessionId}}' }]) },
          { name: 'Process (agent)', request: { method: 'POST', header: [{ key: 'x-agent-key', value: '{{agentKey}}', type: 'text' }, { key: 'x-session-id', value: '{{sessionId}}', type: 'text' }], url: { raw: `${baseUrl}/conversation/agent-process`, host: ['{{baseUrl}}'], path: ['conversation', 'agent-process'] }, body: { mode: 'formdata', formdata: [{ key: 'text', value: 'Hello!', type: 'text' }] } } },
          { name: 'Text chat', request: mkReq('POST', '/conversation/text-chat', [agentH, { 'x-session-id': '{{sessionId}}' }], { message: 'Hello!' }) },
          { name: 'Generate (normalized)', request: mkReq('POST', '/agents/:id/generate', [agentH, { 'x-session-id': '{{sessionId}}' }], { message: 'Hello!' }) },
        ],
      },
      {
        name: 'Standard Mastra API (/api/std)', item: [
          { name: 'OpenAPI schema', request: { method: 'GET', header: [], url: { raw: '{{stdBaseUrl}}/openapi.json', host: ['{{stdBaseUrl}}'], path: ['openapi.json'] } } },
          { name: 'Generate', request: { method: 'POST', header: [{ key: 'Authorization', value: 'Bearer {{agentKey}}', type: 'text' }, { key: 'Content-Type', value: 'application/json', type: 'text' }], url: { raw: '{{stdBaseUrl}}/agents/{{agentId}}/generate', host: ['{{stdBaseUrl}}'], path: ['agents', '{{agentId}}', 'generate'] }, body: { mode: 'raw', raw: JSON.stringify({ messages: [{ role: 'user', content: 'Hello!' }] }, null, 2), options: { raw: { language: 'json' } } } } },
          { name: 'Stream (SSE)', request: { method: 'POST', header: [{ key: 'Authorization', value: 'Bearer {{agentKey}}', type: 'text' }, { key: 'Content-Type', value: 'application/json', type: 'text' }], url: { raw: '{{stdBaseUrl}}/agents/{{agentId}}/stream', host: ['{{stdBaseUrl}}'], path: ['agents', '{{agentId}}', 'stream'] }, body: { mode: 'raw', raw: JSON.stringify({ messages: [{ role: 'user', content: 'Hello!' }] }, null, 2), options: { raw: { language: 'json' } } } } },
        ],
      },
      {
        name: 'S2S (Workspace)', item: [
          { name: 'List templates', request: mkReq('GET', '/s2s/templates', [wsH]) },
          { name: 'Get template', request: mkReq('GET', '/s2s/templates/:id', [wsH]) },
          { name: 'Create agent', request: mkReq('POST', '/s2s/agents', [wsH], { templateId: '...', name: 'S2S Agent' }) },
          { name: 'List agents', request: mkReq('GET', '/s2s/agents', [wsH]) },
          { name: 'Get agent', request: mkReq('GET', '/s2s/agents/:id', [wsH]) },
          { name: 'Update agent', request: mkReq('PUT', '/s2s/agents/:id', [wsH], { name: 'Updated', greeting: 'Hi!' }) },
          { name: 'Delete agent', request: mkReq('DELETE', '/s2s/agents/:id', [wsH]) },
          { name: 'Agent sessions', request: mkReq('GET', '/s2s/agents/:id/sessions', [wsH]) },
          { name: 'Test MCP', request: mkReq('POST', '/s2s/agents/:id/mcp-test', [wsH]) },
        ],
      },
      {
        name: 'Workspaces (Admin)', item: [
          { name: 'Create workspace', request: mkReq('POST', '/workspaces', [authH], { name: 'My Workspace', templateIds: [] }) },
          { name: 'List workspaces', request: mkReq('GET', '/workspaces', [authH]) },
          { name: 'Get workspace', request: mkReq('GET', '/workspaces/:id', [authH]) },
          { name: 'Update workspace', request: mkReq('PUT', '/workspaces/:id', [authH], { name: 'Updated' }) },
          { name: 'Delete workspace', request: mkReq('DELETE', '/workspaces/:id', [authH]) },
        ],
      },
      {
        name: 'Public', item: [
          { name: 'Config', request: mkReq('GET', '/config/public') },
          { name: 'Providers', request: mkReq('GET', '/providers') },
          { name: 'Catalog templates', request: mkReq('GET', '/catalog/templates') },
          { name: 'Catalog template by id', request: mkReq('GET', '/catalog/templates/:id') },
        ],
      },
      {
        name: 'Standalone STT/TTS', item: [
          { name: 'Transcribe', request: { method: 'POST', header: [{ key: 'x-agent-key', value: '{{agentKey}}' }], url: { raw: `${baseUrl}/stt/transcribe`, host: ['{{baseUrl}}'], path: ['stt', 'transcribe'] }, body: { mode: 'formdata', formdata: [{ key: 'audio', type: 'file', src: '' }, { key: 'provider', value: 'deepgram-nova-3', type: 'text' }] } } },
          { name: 'Synthesize', request: mkReq('POST', '/tts/synthesize', [agentH], { text: 'Hello world', provider: 'openai-tts-1' }) },
        ],
      },
    ],
  };
}

function downloadPostmanCollection() {
  const collection = buildPostmanCollection();
  const json = JSON.stringify(collection, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Eazyshow_Live_Agent.postman_collection.json';
  a.click();
  URL.revokeObjectURL(url);
}

export function ApiDocsPage() {
  const [expanded, setExpanded] = useState<Set<number>>(new Set([0]));

  const toggle = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.heading}>API Reference</h2>
          <p style={s.sub}>Integration endpoints for external apps, Twilio, and agent self-service.</p>
        </div>
        <div style={s.actions}>
          <a
            style={s.schemaBtn}
            href={`${API_BASE_URL}/std/openapi.json`}
            target="_blank"
            rel="noreferrer"
            title="Standard Mastra OpenAPI 3.1 schema (served at /api/std/openapi.json)"
          >
            OpenAPI schema ↗
          </a>
          <button style={s.downloadBtn} onClick={downloadPostmanCollection}>
            Download Postman Collection
          </button>
        </div>
      </div>

      {renderedSections.map((sec, i) => (
        <div key={i} style={s.section}>
          <div style={s.sectionHeader} onClick={() => toggle(i)}>
            <strong>{sec.title}</strong>
            <span style={s.arrow}>{expanded.has(i) ? '\u25B2' : '\u25BC'}</span>
          </div>
          {expanded.has(i) && (
            <div style={s.content}>
              <MarkdownText text={sec.content} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  heading: { margin: '0 0 4px', fontSize: 20, color: '#1a1a1a' },
  sub: { margin: 0, fontSize: 13, color: '#888' },
  actions: { display: 'flex', gap: 8, alignItems: 'center' },
  schemaBtn: { padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#f97316', background: '#fff', border: '1px solid #f97316', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none' },
  downloadBtn: { padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#fff', background: '#f97316', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' },
  section: { marginBottom: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fafafa', cursor: 'pointer', fontSize: 14 },
  arrow: { fontSize: 10, color: '#888' },
  content: { padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: '#333', background: '#fff', borderTop: '1px solid #e5e7eb' },
};
